/* eslint-disable promise/prefer-await-to-then */
import { EWS_API_BASE_URL } from '@/core/constants/appConfig/environment';
import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { ScriptHandler } from '@/core/handlers/script';
import { EDestinationType } from '@/core/interfaces';
import { DestinationStatusHandler } from '@/destinations/status';
import { TMetaDestinationCredentials } from '@/destinations/types/destination_creds_types';
import { Logger } from '@/shared/logger/logger';

import {
  DestinationBundle,
  DestinationPayload,
  DestinationSendingResult,
} from '../abstract';
import { transformMableEventToFbEvent } from './event_transformer';
import { FbEventName } from './types';

export class MetaDestinationBundle extends DestinationBundle {
  override allowedEvents: EMableEventName[] = [
    EMableEventName.PAGE_VIEWED,
    EMableEventName.PRODUCT_ADDED,
    EMableEventName.CHECKOUT_STARTED,
    EMableEventName.PAYMENT_INFO_ADDED,
    EMableEventName.PRODUCT_VIEWED,
    EMableEventName.LEAD,
    EMableEventName.ORDER_COMPLETED,
    EMableEventName.CUSTOM_EVENT,
  ];
  pixelStatus!: any;
  override hasBrowserDestination = true;
  override destinationConfig: TMetaDestinationCredentials;
  constructor(
    destinationConfig: TMetaDestinationCredentials,
    destinationId: string,
  ) {
    super(EDestinationType.Facebook, destinationConfig, destinationId);
    if (
      !destinationConfig?.pixelId &&
      !(destinationConfig as any)?.config?.pixelId
    ) {
      throw new Error('Pixel ID is not configured');
    }
    this.destinationConfig = destinationConfig;
    this.pixelStatus = new DestinationStatusHandler({
      destinationUrl: 'https://connect.facebook.net/en_US/fbevents.js',
      storageNamespace: 'metaPixelStatus',
    });
  }
  override getBrowserDestinationSendingStatus(): boolean {
    return this.pixelStatus.isAvailable || false;
  }

  override async init(): Promise<void> {
    // Initialization logic for Destination
    this.pixelStatus = await this.pixelStatus.getStatus();
    this.destinationConfig = {
      ...this.destinationConfig,
    };
    if (!this.destinationConfig.pixelId) {
      Logger.error('Destination ID is not configured');
    }
    // Initialize Destination here
  }
  override async calculatePayload(
    mableEvent: TMableEvent,
  ): Promise<DestinationPayload> {
    return transformMableEventToFbEvent(mableEvent, this.destinationConfig);
  }

  override async sendToDestination(
    params: URLSearchParams,
  ): Promise<DestinationSendingResult> {
    try {
      if (!this.destinationConfig.pixelId) {
        throw new Error('Pixel ID is not configured');
      }

      const fbBaseUrl = 'https://www.facebook.com/tr';
      const ewsBaseUrl = `${EWS_API_BASE_URL}/ews/pixel`;
      params.set('id', this.destinationConfig.pixelId);

      // Create array to store image load promises
      const imageLoadPromises: Promise<boolean>[] = [];

      // Function to create and load an image with given parameters
      const createAndLoadImage = (
        imageParams: URLSearchParams,
        baseUrl: string,
        additionalParams?: Record<string, string>,
      ): Promise<boolean> =>
        new Promise(resolve => {
          const img = new Image(1, 1);
          img.style.display = 'none';

          if (additionalParams) {
            Object.entries(additionalParams).forEach(([key, value]) => {
              imageParams.set(key, value);
            });
          }

          img.src = `${baseUrl}?${imageParams.toString()}`;
          if (document?.body) {
            document.body.append(img);
          } else {
            document.addEventListener('DOMContentLoaded', () => {
              document.body.append(img);
            });
          }
          resolve(true);
        });

      // Send to Facebook
      imageLoadPromises.push(
        createAndLoadImage(new URLSearchParams(params), fbBaseUrl),
      );
      // Send to EWS endpoint with additional parameters
      const ewsParams = new URLSearchParams(params);
      imageLoadPromises.push(
        createAndLoadImage(ewsParams, ewsBaseUrl, {
          sid: 'tbd',
          sourceUID: ScriptHandler.getInstance().getSourceUid(),
        }),
      );
      const success = true;
      // Wait for all images to load
      void Promise.all(imageLoadPromises);

      if (params.get('ev') === FbEventName.PURCHASE) {
        return {
          destinationReqEvent: Object.fromEntries(params),
          reason: success ? '' : 'Failed to load tracking pixels',
          success,
        };
      }

      return {
        destinationReqEvent: {},
        reason: success ? '' : 'Failed to load tracking pixels',
        success,
      };
    } catch (error) {
      return {
        destinationReqEvent: {},
        reason:
          error instanceof Error ? error.message : 'Unknown error occurred',
        success: false,
      };
    }
  }
}
